import {
    getInventoryHealthInbound,
    type InventoryHealthInboundDetail,
    type InventoryHealthInboundLine,
} from '@api/replenishment-planning';
import MacWindowModal, { macWindowClasses as classes } from '@components/ui/MacWindowModal';
import { Badge, Divider, Group, Loader, Stack, Text } from '@mantine/core';
import { Fragment, useEffect, useState, type FC } from 'react';

/**
 * Which half of the inbound number the operator clicked. The endpoint returns every open
 * line either way; this decides which of them the window is answering about, so the panel
 * shows the breakdown of the number that was actually clicked rather than a superset of it.
 */
export type InboundFocus = 'IN_TRANSIT' | 'OPEN' | 'INBOUND';

type Props = {
    productId: string | null;
    focus: InboundFocus | null;
    /**
     * Extra product ids whose lines belong in this window as well — the parents a row
     * absorbed when the grid's break-down toggle is on.
     *
     * The number in the cell is then the row's own inbound PLUS its parents', so a window
     * showing only the row's own lines would visibly fail to add up. The endpoint is
     * per-product and stays exactly as it is; this fetches it once per id and concatenates.
     * In practice the list is empty — across all of basil only a single draft PO sits on a
     * parent SKU — so the extra request happens on the rare row that needs it.
     */
    extraProductIds?: string[];
    /** Scopes every leg behind the window — a material queried as a product returns nothing. */
    itemType?: 'product' | 'material';
    onClose: () => void;
};

type Section = {
    key: string;
    title: string;
    hint: string;
    match: (line: InventoryHealthInboundLine) => boolean;
};

/**
 * IN TRANSIT is one section — the instrument differs (a shipment against a PO, or an
 * internal STN) but the question is the same: what is on the road and when does it land.
 */
const TRANSIT_SECTIONS: Section[] = [
    {
        key: 'transit',
        title: 'In transit',
        hint: 'Shipped and moving — goods have left production',
        match: (line) => line.state === 'IN_TRANSIT',
    },
];

/**
 * OPEN splits by who you chase. A PO leg is a vendor conversation, a work-order leg is a
 * manufacturer one, so they are never merged into a single "in production" number here.
 * "Production order" is the operator's name for a work order — the UI uses theirs.
 */
const PRODUCTION_SECTIONS: Section[] = [
    {
        key: 'production-po',
        title: 'In production · via purchase orders',
        hint: 'Committed on a FIRM mother order, not yet shipped',
        match: (line) => line.state === 'IN_PRODUCTION' && line.source === 'PO',
    },
    {
        key: 'production-wo',
        title: 'In production · via production orders',
        hint: 'Open production-order quantity',
        match: (line) => line.state === 'IN_PRODUCTION' && line.source === 'WO',
    },
];

/**
 * PENDING is committed but unstarted — a blanket contract, or a work order nobody has begun.
 * Kept as its own section rather than merged into production: both are commitments the
 * planner must not re-order, but only one of them is being made right now, and they are
 * chased in completely different conversations.
 */
const PENDING_SECTIONS: Section[] = [
    {
        key: 'pending-po',
        title: 'Pending · via purchase orders',
        hint: 'Unshipped balance of an OPEN (blanket) order — dated at the contract window end',
        match: (line) => line.state === 'PENDING' && line.source === 'PO',
    },
    {
        key: 'pending-wo',
        title: 'Pending · via production orders',
        hint: 'Committed but un-batched — dated on the production order’s due date',
        match: (line) => line.state === 'PENDING' && line.source === 'WO',
    },
];

/** OPEN = everything committed but not yet moving: in production, then pending. */
const OPEN_SECTIONS: Section[] = [...PRODUCTION_SECTIONS, ...PENDING_SECTIONS];

/**
 * INBOUND = what the planner nets as supply: in transit + in production. Pending is
 * deliberately absent — a promise nobody has started is not arriving.
 */
const INBOUND_SECTIONS: Section[] = [...TRANSIT_SECTIONS, ...PRODUCTION_SECTIONS];

const SOURCE_COLOR: Record<InventoryHealthInboundLine['source'], string> = {
    PO: 'blue',
    STN: 'grape',
    WO: 'teal',
};

const TITLE: Record<InboundFocus, string> = {
    IN_TRANSIT: 'In transit',
    OPEN: 'Open',
    INBOUND: 'Inbound',
};

const SECTIONS_FOR: Record<InboundFocus, Section[]> = {
    IN_TRANSIT: TRANSIT_SECTIONS,
    OPEN: OPEN_SECTIONS,
    INBOUND: INBOUND_SECTIONS,
};

/**
 * Breaks half of the Inbound number into the documents it is made of and WHEN each slice
 * lands, in a macOS-style window.
 *
 * Sections with nothing in them are not rendered — a brand that never runs production
 * orders should not have to read a permanent zero to learn that.
 */
const InboundDetailPanel: FC<Props> = ({
    productId,
    focus,
    extraProductIds,
    itemType = 'product',
    onClose,
}) => {
    const [detail, setDetail] = useState<InventoryHealthInboundDetail | null>(null);
    const [loading, setLoading] = useState(false);

    // Joined rather than passed as an array so the effect's dependency is a primitive — an
    // array literal from the parent would be a new reference every render and refetch forever.
    const extraIdsKey = (extraProductIds ?? []).join(',');

    useEffect(() => {
        if (!productId) {
            setDetail(null);
            return;
        }
        const ids = [productId, ...(extraIdsKey ? extraIdsKey.split(',') : [])];
        let cancelled = false;
        setLoading(true);
        Promise.all(ids.map((id) => getInventoryHealthInbound(id, itemType)))
            .then((results) => {
                if (cancelled) return;
                // Concatenated, not merged: two ids never describe the same document, and a
                // line keeps naming the instrument it actually sits on — which is the point.
                // A unit that came in via the clearance listing's PO should say so.
                setDetail({
                    productId,
                    lines: results.flatMap((result) => result.lines ?? []),
                    total: results.reduce((sum, result) => sum + (result.total ?? 0), 0),
                });
            })
            .catch(() => {
                if (!cancelled) setDetail(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [productId, extraIdsKey, itemType]);

    // Column headers follow the leading section: a transit line names a shipment and its
    // mother order, everything else names a document and its status.
    const isTransit = focus === 'IN_TRANSIT' || focus === 'INBOUND';
    const sections = (focus ? SECTIONS_FOR[focus] : [])
        .map((section) => {
            const lines = (detail?.lines ?? [])
                .filter(section.match)
                // Undated last: real quantity, unknown arrival — better than sorting it
                // under a date we do not have.
                .sort((a, b) =>
                    (a.expectedDate ?? '9999-12-31').localeCompare(
                        b.expectedDate ?? '9999-12-31',
                    ),
                );
            return {
                section,
                lines,
                total: lines.reduce((sum, line) => sum + line.quantity, 0),
            };
        })
        .filter((entry) => entry.lines.length > 0);

    const focusedTotal = sections.reduce((sum, entry) => sum + entry.total, 0);
    const lineCount = sections.reduce((sum, entry) => sum + entry.lines.length, 0);

    return (
        <MacWindowModal
            opened={productId !== null && focus !== null}
            onClose={onClose}
            title={`${focus ? TITLE[focus] : ''} — ${productId}`}
        >
                    {loading && (
                        <Group justify="center" py="xl">
                            <Loader size="sm" />
                        </Group>
                    )}

                    {!loading && sections.length === 0 && (
                        <Text size="sm" c="dimmed">
                            {isTransit
                                ? 'Nothing on the road for this item right now.'
                                : 'Nothing open for this item — everything committed has already shipped.'}
                        </Text>
                    )}

                    {!loading && sections.length > 0 && (
                        <Stack gap="lg">
                            <Text size="xs" c="dimmed">
                                {focusedTotal} units across {lineCount} line(s)
                            </Text>

                            {sections.map(({ section, lines, total }) => (
                                <Stack key={section.key} gap={8}>
                                    <Group justify="space-between" wrap="nowrap" align="flex-end">
                                        <Stack gap={0}>
                                            <Text size="13px" fw={600}>
                                                {section.title}
                                            </Text>
                                            <Text size="xs" c="dimmed">
                                                {section.hint}
                                            </Text>
                                        </Stack>
                                        <Text size="13px" fw={600}>
                                            {total}
                                        </Text>
                                    </Group>
                                    <Divider />

                                    <div className={classes.rows}>
                                        <div className={classes.columnHeader}>
                                            {isTransit ? 'Shipment' : 'Document'}
                                        </div>
                                        <div className={classes.columnHeader}>
                                            {isTransit ? 'Mother PO' : 'Status'}
                                        </div>
                                        <div className={classes.columnHeader}>
                                            {isTransit ? 'Expected arrival' : 'Expected ready'}
                                        </div>
                                        <div
                                            className={`${classes.columnHeader} ${classes.numeric}`}
                                        >
                                            Qty
                                        </div>

                                        {lines.map((line, index) => (
                                            <Fragment
                                                key={`${line.reference ?? 'untracked'}-${index}`}
                                            >
                                                <Group gap={8} wrap="nowrap">
                                                    <Badge
                                                        size="xs"
                                                        variant="light"
                                                        color={SOURCE_COLOR[line.source]}
                                                    >
                                                        {line.source === 'WO' ? 'PROD' : line.source}
                                                    </Badge>
                                                    <Text size="13px" lineClamp={1}>
                                                        {line.reference ?? 'No document'}
                                                    </Text>
                                                </Group>
                                                <Text
                                                    size="13px"
                                                    c={
                                                        (isTransit
                                                            ? line.motherReference
                                                            : line.status)
                                                            ? undefined
                                                            : 'dimmed'
                                                    }
                                                    lineClamp={1}
                                                >
                                                    {(isTransit
                                                        ? line.motherReference
                                                        : line.status) ?? '—'}
                                                </Text>
                                                {/* An absent date is stated as absent. The
                                                    alternative — defaulting to today — reads as
                                                    "arriving now", a claim the instrument does
                                                    not support. */}
                                                <Text
                                                    size="13px"
                                                    c={line.expectedDate ? undefined : 'dimmed'}
                                                >
                                                    {line.expectedDate ?? 'Not scheduled'}
                                                </Text>
                                                <Text size="13px" className={classes.numeric}>
                                                    {line.quantity}
                                                </Text>
                                            </Fragment>
                                        ))}
                                    </div>
                                </Stack>
                            ))}
                        </Stack>
                    )}
        </MacWindowModal>
    );
};

export default InboundDetailPanel;
